import { type AccountDetailForSelector } from '@varo/graphql-schema-types';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PaymentAccountRow from 'shared/components/lending/PaymentAccountRow/PaymentAccountRow';
import { type PaymentPlanReviewLink } from 'shared/components/lending/paymentPlanReview/PaymentPlanReviewLayout/PaymentPlanReviewLayout';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

interface PaymentPlanAutopayProps {
  sectionTitle: string;
  isEnabled: boolean;
  isDisabled: boolean;
  onChange: (isEnabled: boolean) => void;
  isAccountLoading: boolean;
  account?: AccountDetailForSelector | null;
  infoLink?: PaymentPlanReviewLink;
}

const PaymentPlanAutopay = ({
  sectionTitle,
  isEnabled,
  isDisabled,
  onChange,
  isAccountLoading,
  account,
  infoLink,
}: PaymentPlanAutopayProps) => {
  const { t: translatedText } = useTranslation('lending', {
    keyPrefix: 'paymentPlanReview',
  });

  return (
    <View style={styles.container}>
      <SectionDivider labelText={sectionTitle} />
      <ListItem
        primaryCopy={translatedText('autopayLabel')}
        showsChevron={false}
        trailingAccessory={{
          id: 'payment-plan-autopay-toggle',
          name: VXLComponentId.SWITCH,
          props: {
            isOn: isEnabled,
            showStatusLabel: true,
            onValueChange: onChange,
            isDisabled,
          },
        }}
        trailingAccessoryContainerStyle={styles.toggleContainer}
        accessibilityLabel={translatedText('autopayLabel')}
      />
      <View style={styles.divider}>
        <HairLineDivider />
      </View>
      {isAccountLoading ? (
        <View style={styles.accountLoading}>
          <LoadingSpinner />
        </View>
      ) : null}
      {isEnabled && !isAccountLoading && account ? (
        <PaymentAccountRow
          account={account}
          secondaryCopy={`${translatedText('available')} ${account.availableBalance.formatted}`}
        />
      ) : null}
      <View style={styles.descriptionContainer}>
        <BodySmText numberOfLines={0} style={styles.description}>
          {translatedText('autopayDescription')}
          {infoLink ? (
            <>
              {' '}
              <HyperlinkBaseText
                onPress={infoLink.onPress}
                analyticsId={infoLink.analyticsId}
              >
                {infoLink.label}
              </HyperlinkBaseText>
            </>
          ) : null}
        </BodySmText>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  accountLoading: { alignItems: 'center', paddingVertical: theme.spacing.m },
  container: { paddingBottom: theme.spacing.s },
  description: { color: theme.color.text.secondary },
  descriptionContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  divider: { marginHorizontal: theme.spacing.m },
  toggleContainer: { flexShrink: 0 },
});

export default PaymentPlanAutopay;
